import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { TrendingUp, Loader2, Package, RefreshCw } from "lucide-react";
import { useExpenseNotifications } from "@/hooks/useExpenseNotifications";
import { Card } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useTripletexIntegration } from "@/hooks/useTripletexIntegration";
import { useFinagoIntegration } from "@/hooks/useFinagoIntegration";
import { usePowerOfficeIntegration } from "@/hooks/usePowerOfficeIntegration";
import { useBusinessCentralIntegration } from "@/hooks/useBusinessCentralIntegration";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getActiveWorkspaceId } from "@/utils/workspace-helpers";
import { useTranslation } from "react-i18next";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";

export function ExpenseNotificationCenter() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { 
    useNewExpensesCountQuery,
    usePendingContractsCountQuery,
    useNewExpensesQuery,
    usePendingContractsQuery,
    useMarkExpensesViewedMutation,
  } = useExpenseNotifications();
  
  const { useHasActiveTripletexQuery, useLastSyncTimeQuery: useTripletexLastSyncTimeQuery } = useTripletexIntegration();
  const { useHasActiveFinagoQuery, useLastSyncTimeQuery: useFinagoLastSyncTimeQuery } = useFinagoIntegration();
  const { useHasActivePowerOfficeQuery } = usePowerOfficeIntegration();
  const { useHasActiveBusinessCentralQuery } = useBusinessCentralIntegration();
  
  const { data: hasActiveTripletex } = useHasActiveTripletexQuery();
  const { data: hasActiveFinago } = useHasActiveFinagoQuery();
  const { data: hasActivePowerOffice } = useHasActivePowerOfficeQuery();
  const { data: hasActiveBusinessCentral } = useHasActiveBusinessCentralQuery();
  const { data: tripletexLastSyncTime, refetch: refetchTripletexLastSyncTime } = useTripletexLastSyncTimeQuery();
  const { data: finagoLastSyncTime, refetch: refetchFinagoLastSyncTime } = useFinagoLastSyncTimeQuery();
  
  // Feature access - check if user has contract management feature
  const { canUseContractManagement, canUseSalesAnalyticsFeature, isLoading: isLoadingFeatureAccess } = useFeatureAccess();
  
  const { data: expensesCount = 0 } = useNewExpensesCountQuery();
  const { data: pendingCount = 0 } = usePendingContractsCountQuery();
  const { data: expenses = [], isLoading: expensesLoading } = useNewExpensesQuery();
  const { data: pendingContracts = [], isLoading: pendingLoading } = usePendingContractsQuery();
  const markAsViewed = useMarkExpensesViewedMutation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [isRunningSync, setIsRunningSync] = useState(false);

  // For users without contract management, don't count expenses/contracts
  const effectiveExpensesCount = canUseContractManagement ? expensesCount : 0;
  const effectivePendingCount = canUseContractManagement ? pendingCount : 0;
  const totalCount = effectiveExpensesCount + effectivePendingCount;
  const hasActiveErp = hasActiveTripletex || hasActiveFinago || hasActivePowerOffice || hasActiveBusinessCentral;

  // Use the most recent sync time from either ERP
  const lastSyncTime = tripletexLastSyncTime && finagoLastSyncTime
    ? (tripletexLastSyncTime > finagoLastSyncTime ? tripletexLastSyncTime : finagoLastSyncTime)
    : tripletexLastSyncTime || finagoLastSyncTime;

  // Don't show if no active ERP integration or still loading features
  if (!hasActiveErp || isLoadingFeatureAccess) {
    return null;
  }
  
  // For users without contract management AND without sales analytics, hide completely
  if (!canUseContractManagement && !canUseSalesAnalyticsFeature) {
    return null;
  }

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen && expensesCount > 0) {
      // Mark expenses as viewed when opening (updates badge count only)
      markAsViewed.mutate();
    }
    if (!newOpen) {
      // Refresh the expenses list when closing so it's fresh next time
      queryClient.invalidateQueries({ queryKey: ["new-expenses"] });
    }
  };

  const handleExpenseClick = (contractId: string) => {
    navigate(`/contracts/${contractId}`);
    setOpen(false);
  };

  const handleViewAll = () => {
    // Navigate to the unified pending contracts page
    navigate("/erp-integration/pending-contracts");
    setOpen(false);
  };

  const handleRunSyncNow = async () => {
    setIsRunningSync(true);
    try {
      let totalSyncedExpenses = 0;
      let totalDetectedContracts = 0;

      // Sync Tripletex if active
      if (hasActiveTripletex) {
        const { data, error } = await supabase.functions.invoke('tripletex-daily-sync', {
          body: { workspace_id: getActiveWorkspaceId() },
        });

        if (error) throw error;
        totalSyncedExpenses += data.syncedExpenses || 0;
        totalDetectedContracts += data.detectedPotentialContracts || 0;
        await refetchTripletexLastSyncTime();
      }

      // Sync Finago if active
      if (hasActiveFinago) {
        const { data, error } = await supabase.functions.invoke('finago-daily-sync', {
          body: { workspace_id: getActiveWorkspaceId() },
        });

        if (error) throw error;
        totalSyncedExpenses += data.syncedExpenses || 0;
        totalDetectedContracts += data.detectedPotentialContracts || 0;
        await refetchFinagoLastSyncTime();
      }

      toast.success(`Sync completed! ${totalSyncedExpenses} expenses synced, ${totalDetectedContracts} potential contracts detected.`);
    } catch (error: any) {
      console.error('Sync error:', error);
      toast.error(`Sync failed: ${error.message}`);
    } finally {
      setIsRunningSync(false);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <Popover open={open} onOpenChange={handleOpenChange}>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button variant="ghost" size="icon" className="relative">
                <TrendingUp className="h-5 w-5" />
                {totalCount > 0 && (
                  <Badge 
                    className="absolute -top-2 -right-2 px-1.5 py-0.5 min-w-[1.2rem] h-5 flex items-center justify-center bg-primary"
                  >
                    {totalCount > 99 ? "99+" : totalCount}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>
            <p>{t('header.erpNotifications', 'ERP Notifications')}</p>
          </TooltipContent>
        <PopoverContent className="w-96 p-0" align="end">
          <div className="p-3 border-b space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold">{t('header.erpNotifications', 'ERP Notifications')}</h3>
              {canUseContractManagement && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={handleViewAll}
                  className="text-xs h-7"
                >
                  {t('common.viewAll', 'View all')}
                </Button>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              {lastSyncTime && (
                <p className="text-xs text-muted-foreground">
                  {t('erp.lastSync', 'Last sync')}: {format(lastSyncTime, "MMM d, yyyy 'at' HH:mm")}
                </p>
              )}
            </div>
          </div>
          
          {/* Show tabs only if user has contract management feature */}
          {canUseContractManagement ? (
            <Tabs defaultValue="expenses" className="w-full">
              <TabsList className="w-full grid grid-cols-2 rounded-none border-b">
                <TabsTrigger value="expenses" className="relative">
                  {t('erp.newExpenses', 'New Expenses')}
                  {effectiveExpensesCount > 0 && (
                    <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 h-5 text-xs">
                      {effectiveExpensesCount}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger value="pending" className="relative">
                  {t('erp.potentialContracts', 'Potential Contracts')}
                  {effectivePendingCount > 0 && (
                    <Badge variant="secondary" className="ml-2 px-1.5 py-0.5 h-5 text-xs">
                      {effectivePendingCount}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="expenses" className="m-0">
                <ScrollArea className="h-[400px]">
                  {expensesLoading ? (
                    <div className="p-4 space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="p-3 border-l-4 border-l-muted">
                          <div className="flex justify-between items-start mb-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                          <div className="flex justify-between items-center">
                            <Skeleton className="h-3 w-48" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : expenses.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t('erp.noNewExpenses', 'No new expenses')}</p>
                    </div>
                  ) : (
                    <div>
                      {expenses.map((expense) => (
                        <Card 
                          key={expense.id}
                          className="m-2 p-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-4 border-l-primary"
                          onClick={() => handleExpenseClick(expense.contractId)}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <h4 className="font-medium">{expense.supplierName}</h4>
                            <span className="text-xs text-muted-foreground">
                              {format(expense.transactionDate, "MMM d")}
                            </span>
                          </div>
                          <div className="flex justify-between items-center">
                            <p className="text-sm text-muted-foreground">
                              {expense.description || t('erp.expenseDetected', 'Expense detected')}
                            </p>
                            <span className="text-sm font-semibold">
                              {new Intl.NumberFormat('no-NO', {
                                style: 'currency',
                                currency: 'NOK',
                              }).format(expense.amount)}
                            </span>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>

              <TabsContent value="pending" className="m-0">
                <ScrollArea className="h-[400px]">
                  {pendingLoading ? (
                    <div className="p-4 space-y-3">
                      {[1, 2, 3].map((i) => (
                        <Card key={i} className="p-3 border-l-4 border-l-muted">
                          <div className="flex justify-between items-start mb-2">
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-3 w-16" />
                          </div>
                          <div className="flex justify-between items-center">
                            <Skeleton className="h-3 w-48" />
                            <Skeleton className="h-3 w-20" />
                          </div>
                        </Card>
                      ))}
                    </div>
                  ) : pendingContracts.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Package className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p>{t('erp.noPotentialContracts', 'No potential contracts')}</p>
                    </div>
                  ) : (
                    <div>
                    {pendingContracts.map((contract) => {
                      const isSales = contract.detection_type === 'sales';
                      return (
                        <Card 
                          key={contract.id}
                          className={`m-2 p-3 cursor-pointer hover:bg-muted/50 transition-colors border-l-4 ${isSales ? 'border-l-green-500' : 'border-l-orange-500'}`}
                          onClick={() => {
                            navigate(`/erp-integration/pending-contracts?highlight=${contract.id}`);
                            setOpen(false);
                          }}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <div className="flex items-center gap-1.5">
                              <h4 className="font-medium">{contract.supplier_name}</h4>
                              {isSales && (
                                <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                  {t('salesDetection')}
                                </Badge>
                              )}
                            </div>
                            <Badge variant="secondary" className="text-xs shrink-0">
                              {contract.confidence_score.toFixed(0)}% {t('common.confidence', 'confidence')}
                            </Badge>
                          </div>
                          <div className="flex justify-between items-center mb-1">
                            <p className="text-sm text-muted-foreground">
                              {isSales ? t('recurringCustomer') : t('erp.detected', 'Detected')} {format(contract.detected_at, "MMM d")}
                            </p>
                            <span className="text-sm font-semibold">
                              ~{new Intl.NumberFormat('no-NO', {
                                style: 'currency',
                                currency: contract.currency_code,
                              }).format(contract.average_amount)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <Badge variant="outline" className="text-xs capitalize">
                              {contract.erp_source}
                            </Badge>
                            {contract.client_name && (
                              <span className="text-xs text-muted-foreground truncate">
                                {contract.client_name}
                              </span>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                    </div>
                  )}
                </ScrollArea>
              </TabsContent>
            </Tabs>
          ) : (
            // For users without contract management, show a simple message
            <div className="p-8 text-center text-muted-foreground">
              <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>{t('erp.salesAnalyticsOnly', 'ERP connected for Sales Analytics')}</p>
              <p className="text-xs mt-2">{t('erp.syncFromIntegrations', 'Sync data from the Integrations page')}</p>
            </div>
          )}
        </PopoverContent>
      </Popover>
      </Tooltip>
    </TooltipProvider>
  );
}